import {
  IconButton,
  List,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ChatChannelType, IChatConversation } from 'src/@types/chat';
import { CustomTextField } from 'src/components/custom-input';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useSelector } from 'src/redux/store';
import { ChatSelectorItemIcon } from './ChatSelectorItem';

type Props = {
  selectChat: (chat: IChatConversation) => void;
  backToRoot: () => void;
  selectedArray: string[];
};

export const AddChatSelector = ({
  selectChat,
  backToRoot,
  selectedArray = [],
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { translate } = useLocales();
  const channelsMap = useSelector((state) => state.chat.conversations.byId);

  const channelsList = useMemo(
    () =>
      Object.values(channelsMap)
        .filter((chat) => chat.type === ChatChannelType.TEXT)
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
        .sort((a, b) => {
          if (a.p2p && !b.p2p) return -1;
          return 0;
        }),
    [channelsMap],
  );

  const isSelected = useCallback(
    (chat: IChatConversation) => selectedArray.includes(chat._id),
    [selectedArray],
  );

  const handleClick = (e: React.MouseEvent, chat: IChatConversation) => {
    e.stopPropagation();
    selectChat(chat);
    // popupDocsState.close();
  };

  const handleBack = () => {
    backToRoot();
  };

  const searchedChats = useMemo(() => {
    if (!searchTerm || !channelsList?.length) return [];
    return channelsList?.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, channelsList]);

  const chatList = useMemo(
    () => (searchTerm ? searchedChats : channelsList),
    [searchedChats, searchTerm, channelsList],
  );

  return (
    <List className="sublist">
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={handleBack}>
          <Iconify icon="tabler:arrow-left" />
        </IconButton>

        <StyledInput
          InputProps={{
            startAdornment: <Iconify icon="tabler:search" />,
          }}
          placeholder={translate('shared.search.find_a_chat')}
          fullWidth
          className={'input'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Stack>
      <List className="sublist">
        {searchTerm && !searchedChats?.length && (
          <Typography variant="caption" color="textSecondary" component="div">
            {translate('shared.labels.no_conversation_found')}
          </Typography>
        )}
        {chatList?.map((chat) => (
          <MenuItem
            key={chat._id}
            onClick={(e) => handleClick(e, chat)}
            sx={{ gap: 2 }}
          >
            <ChatSelectorItemIcon chat={chat} />
            <ListItemText>{chat.name}</ListItemText>
            {isSelected(chat) && (
              <Iconify icon="tabler:check" color="success.main" />
            )}
          </MenuItem>
        ))}
      </List>
    </List>
  );
};

const StyledInput = styled(CustomTextField)(({ theme }) => ({
  mb: theme.spacing(1),
  '&.input': {
    '& .MuiInputBase-input': {
      padding: `${theme.spacing(1)} !important`,
    },
  },
}));
